import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useState, useEffect, useRef } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import { useAuth } from '../../../hooks/useAuth';
import {
  listTasks,
  createTask,
  updateTask,
  deleteTask,
  type Task,
  type TaskStatus,
  type TaskPriority,
  PRIORITY_COLORS,
  PRIORITY_DOT,
  STATUS_LABELS,
} from '../../../lib/tasks';
import { listUsers, type UserDto } from '../../../lib/users';
import { DEFAULT_COMPANY_ID } from '../../../lib/config';

export const Route = createFileRoute('/kinerja/tasks/sprints/$sprintId')({
  component: SprintBoardPage,
  validateSearch: (search: Record<string, unknown>) => ({
    projectId: (search.projectId as string) ?? '',
  }),
});

function SprintBoardPage() {
  return (
    <ProtectedRoute minimumRole="employee">
      <SprintBoardContent />
    </ProtectedRoute>
  );
}

const COLUMNS: { status: TaskStatus; label: string; color: string }[] = [
  { status: 'todo', label: 'Todo', color: 'border-gray-300' },
  { status: 'in_progress', label: 'In Progress', color: 'border-blue-400' },
  { status: 'in_review', label: 'In Review', color: 'border-amber-400' },
  { status: 'done', label: 'Done', color: 'border-green-500' },
];

const PRIORITIES: TaskPriority[] = ['low', 'medium', 'high', 'critical'];

function SprintBoardContent() {
  const { sprintId } = Route.useParams();
  const { projectId } = Route.useSearch();
  const { user } = useAuth();
  const navigate = useNavigate();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;
  const canDelete = user?.role && ['admin', 'hr', 'manager', 'super_admin'].includes(user.role);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [users, setUsers] = useState<UserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Slide-over state (for creating / editing a task)
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showCreate, setShowCreate] = useState<TaskStatus | null>(null);

  const fetchTasks = async () => {
    try {
      const data = await listTasks(sprintId);
      setTasks(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
    listUsers(companyId, { isActive: true }).then((res) => setUsers(res.data?.users ?? [])).catch(() => {});
  }, [sprintId]);

  const handleStatusChange = async (taskId: string, newStatus: TaskStatus) => {
    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t)));
    try {
      await updateTask(taskId, { status: newStatus });
    } catch {
      fetchTasks(); // revert on error
    }
  };

  const handleDelete = async (taskId: string) => {
    if (!confirm('Delete this task?')) return;
    try {
      await deleteTask(taskId);
      toast.success('Task deleted');
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      if (selectedTask?.id === taskId) setSelectedTask(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete task');
      setError(e instanceof Error ? e.message : 'Failed');
    }
  };

  const getUserName = (userId: string | null) => {
    if (!userId) return null;
    return users.find((u) => u.id === userId)?.name ?? userId.slice(0, 8);
  };

  const columnTasks = (status: TaskStatus) => tasks.filter((t) => t.status === status);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-3 shrink-0">
        <button
          onClick={() => navigate({
            to: '/kinerja/tasks/projects/$projectId/',
            params: { projectId: projectId || '' },
          })}
          className="text-gray-400 hover:text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-gray-900">Sprint Board</h1>
          <p className="text-xs text-gray-400 font-mono">{sprintId}</p>
        </div>
        <div className="text-sm text-gray-500">{tasks.length} tasks</div>
      </div>

      {error && (
        <div className="mx-6 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      {/* Kanban board */}
      <div className="flex-1 overflow-x-auto">
        <div className="flex gap-4 p-6 min-w-max h-full">
          {COLUMNS.map((col) => (
            <div key={col.status} className="flex flex-col w-72 shrink-0">
              {/* Column header */}
              <div className={`flex items-center justify-between px-3 py-2 bg-white rounded-t-lg border-t-2 border-l border-r ${col.color} border-b-0`}>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-gray-900">{col.label}</span>
                  <span className="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
                    {columnTasks(col.status).length}
                  </span>
                </div>
                <button
                  onClick={() => setShowCreate(col.status)}
                  className="text-gray-400 hover:text-teal-600 transition-colors"
                  title="Add task"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>

              {/* Tasks */}
              <div className={`flex-1 min-h-48 bg-gray-50 rounded-b-lg border border-t-0 border-gray-200 p-2 space-y-2 overflow-y-auto`}>
                {columnTasks(col.status).map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    getUserName={getUserName}
                    onStatusChange={handleStatusChange}
                    onDelete={canDelete ? handleDelete : undefined}
                    onOpen={() => setSelectedTask(task)}
                    allStatuses={COLUMNS.map((c) => c.status)}
                  />
                ))}

                {/* Quick-add inline */}
                {showCreate === col.status && (
                  <QuickAddTask
                    sprintId={sprintId}
                    projectId={projectId || ''}
                    status={col.status}
                    users={users}
                    onClose={() => setShowCreate(null)}
                    onCreated={(task) => {
                      setTasks((prev) => [...prev, task]);
                      setShowCreate(null);
                    }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Task detail slide-over */}
      {selectedTask && (
        <TaskSlideOver
          task={selectedTask}
          users={users}
          onClose={() => setSelectedTask(null)}
          onUpdated={(updated) => {
            setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
            setSelectedTask(updated);
          }}
          onDeleted={canDelete ? (id) => { handleDelete(id); setSelectedTask(null); } : undefined}
        />
      )}
    </div>
  );
}

// ── Task Card ─────────────────────────────────────────────────────────────────

function TaskCard({
  task,
  getUserName,
  onStatusChange,
  onDelete,
  onOpen,
  allStatuses,
}: {
  task: Task;
  getUserName: (id: string | null) => string | null;
  onStatusChange: (id: string, status: TaskStatus) => void;
  onDelete?: (id: string) => void;
  onOpen: () => void;
  allStatuses: TaskStatus[];
}) {
  const assigneeName = getUserName(task.assigneeUserId);
  const isOverdue =
    task.dueDate && task.status !== 'done' && new Date(task.dueDate) < new Date();

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
      onClick={onOpen}
    >
      <div className="flex items-start gap-2">
        <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${PRIORITY_DOT[task.priority]}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 leading-tight line-clamp-2">{task.title}</p>
        </div>
        {onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(task.id); }}
            className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-400 transition-all shrink-0"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${PRIORITY_COLORS[task.priority]}`}>
          {task.priority}
        </span>
        {task.storyPoints !== null && (
          <span className="text-xs bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded font-medium">
            {task.storyPoints} pts
          </span>
        )}
        {isOverdue && (
          <span className="text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded font-medium">overdue</span>
        )}
        {task.dueDate && !isOverdue && (
          <span className="text-xs text-gray-400">{new Date(task.dueDate).toLocaleDateString()}</span>
        )}
      </div>

      <div className="mt-2.5 flex items-center justify-between">
        {assigneeName ? (
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 text-xs font-bold">
              {assigneeName[0].toUpperCase()}
            </div>
            <span className="text-xs text-gray-500 truncate max-w-20">{assigneeName}</span>
          </div>
        ) : (
          <span className="text-xs text-gray-300">Unassigned</span>
        )}

        {/* Move buttons */}
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
          {allStatuses.filter((s) => s !== task.status).map((s) => (
            <button
              key={s}
              onClick={() => onStatusChange(task.id, s)}
              title={`Move to ${STATUS_LABELS[s]}`}
              className="text-xs text-gray-400 hover:text-teal-600 transition-colors px-1"
            >
              → {STATUS_LABELS[s].split(' ')[0]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Quick Add Task ────────────────────────────────────────────────────────────

function QuickAddTask({
  sprintId, projectId, status, users, onClose, onCreated,
}: {
  sprintId: string;
  projectId: string;
  status: TaskStatus;
  users: UserDto[];
  onClose: () => void;
  onCreated: (task: Task) => void;
}) {
  const [title, setTitle] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [points, setPoints] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setLoading(true);
    try {
      let task = await createTask(sprintId, {
        projectId,
        title: title.trim(),
        assigneeUserId: assigneeId || undefined,
        storyPoints: points ? parseFloat(points) : undefined,
      });
      // If dropped in a non-todo column, move it there immediately
      if (status !== 'todo') {
        task = await updateTask(task.id, { status });
      }
      toast.success('Task created');
      onCreated(task);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to create task');
    }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-teal-300 p-3 space-y-2 shadow-sm">
      <input
        ref={inputRef}
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Task title..."
        className="w-full text-sm border-0 outline-none text-gray-900 placeholder-gray-400"
      />
      <div className="flex gap-2">
        <select
          value={assigneeId}
          onChange={(e) => setAssigneeId(e.target.value)}
          className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 text-gray-600"
        >
          <option value="">Unassigned</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <input
          type="number"
          value={points}
          onChange={(e) => setPoints(e.target.value)}
          placeholder="pts"
          min="0"
          step="0.5"
          className="w-16 text-xs border border-gray-200 rounded px-2 py-1 text-center text-gray-600"
        />
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onClose} className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1">
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || !title.trim()}
          className="text-xs bg-teal-600 text-white px-3 py-1 rounded font-medium disabled:opacity-50"
        >
          {loading ? '...' : 'Add'}
        </button>
      </div>
    </form>
  );
}

// ── Task Slide-over ───────────────────────────────────────────────────────────

function TaskSlideOver({
  task, users, onClose, onUpdated, onDeleted,
}: {
  task: Task;
  users: UserDto[];
  onClose: () => void;
  onUpdated: (task: Task) => void;
  onDeleted?: (id: string) => void;
}) {
  const [form, setForm] = useState({
    title: task.title,
    description: task.description ?? '',
    assigneeUserId: task.assigneeUserId ?? '',
    status: task.status,
    priority: task.priority,
    storyPoints: task.storyPoints !== null ? String(task.storyPoints) : '',
    dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const updated = await updateTask(task.id, {
        title: form.title,
        description: form.description || undefined,
        assigneeUserId: form.assigneeUserId || undefined,
        status: form.status,
        priority: form.priority,
        storyPoints: form.storyPoints ? parseFloat(form.storyPoints) : undefined,
        dueDate: form.dueDate || undefined,
      });
      toast.success('Task updated');
      onUpdated(updated);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to save';
      toast.error(msg);
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/30" onClick={onClose} />

      {/* Panel */}
      <div className="w-96 bg-white border-l border-gray-200 flex flex-col shadow-xl">
        {/* Panel header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">Task Detail</h2>
          <div className="flex items-center gap-2">
            {onDeleted && (
              <button
                onClick={() => onDeleted(task.id)}
                className="text-gray-400 hover:text-red-500 transition-colors"
                title="Delete task"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Panel body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {error && <p className="text-sm text-red-600">{error}</p>}

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
              placeholder="Add details..."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as TaskStatus }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                {COLUMNS.map((c) => <option key={c.status} value={c.status}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value as TaskPriority }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Story Points</label>
              <input
                type="number"
                value={form.storyPoints}
                onChange={(e) => setForm((f) => ({ ...f, storyPoints: e.target.value }))}
                min="0" step="0.5"
                placeholder="—"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Due Date</label>
              <input
                type="date"
                value={form.dueDate}
                onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Assignee</label>
            <select
              value={form.assigneeUserId}
              onChange={(e) => setForm((f) => ({ ...f, assigneeUserId: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">Unassigned</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>

          <div className="text-xs text-gray-400 space-y-1 pt-2 border-t border-gray-100">
            <p>Created: {new Date(task.createdAt).toLocaleString()}</p>
            {task.completedAt && <p>Completed: {new Date(task.completedAt).toLocaleString()}</p>}
          </div>
        </div>

        {/* Panel footer */}
        <div className="px-5 py-4 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
